import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ApplicationRef, Component, NgZone, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';

import { ApiService } from '@core/api/api.service';
import {
  NamePreviewResult,
  StorefrontNameCandidate,
  StorefrontSizeMismatch
} from '@core/models/models';

type NamePreviewView = 'lookup' | 'size-mismatches';

@Component({
  selector: 'app-name-preview-admin',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './name-preview-admin.component.html',
  styleUrl: './name-preview-admin.component.css'
})
export class NamePreviewAdminComponent {
  private readonly api = inject(ApiService);
  private readonly zone = inject(NgZone);
  private readonly appRef = inject(ApplicationRef);

  view: NamePreviewView = 'lookup';
  barcode = '';
  loading = false;
  fetchingMissing = false;
  saving = false;
  errorMessage = '';
  statusMessage = '';
  result: NamePreviewResult | null = null;
  manualDraft = '';

  mismatchesLoading = false;
  mismatchesError = '';
  mismatches: StorefrontSizeMismatch[] = [];
  mismatchesTotal = 0;
  mismatchesOffset = 0;
  readonly mismatchesPageSize = 100;

  switchView(view: NamePreviewView): void {
    this.view = view;
    this.errorMessage = '';
    this.statusMessage = '';
    this.mismatchesError = '';
    if (view === 'size-mismatches' && this.mismatches.length === 0 && !this.mismatchesLoading) {
      void this.loadMismatches(0);
    }
  }

  async loadMismatches(offset = 0): Promise<void> {
    this.mismatchesLoading = true;
    this.mismatchesError = '';
    try {
      const page = await firstValueFrom(
        this.api.listNamePreviewSizeMismatches(this.mismatchesPageSize, offset)
      );
      this.finishMismatches(() => {
        this.mismatches = page.items ?? [];
        this.mismatchesTotal = page.total ?? 0;
        this.mismatchesOffset = page.offset ?? offset;
      });
    } catch {
      this.finishMismatches(() => {
        this.mismatchesError = 'טעינת רשימת אי-ההתאמות נכשלה.';
      });
    }
  }

  async openMismatch(row: StorefrontSizeMismatch): Promise<void> {
    this.barcode = row.barcode;
    this.view = 'lookup';
    this.mismatchesError = '';
    await this.preview();
  }

  async preview(): Promise<void> {
    const code = this.barcode.trim();
    if (code.length === 0) {
      this.errorMessage = 'נא להזין ברקוד.';
      this.result = null;
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    this.statusMessage = '';
    this.result = null;
    try {
      const preview = await firstValueFrom(this.api.previewProductName(code));
      this.finish(() => {
        this.applyResult(preview);
      });
    } catch (error) {
      this.finish(() => {
        if (error instanceof HttpErrorResponse && error.status === 404) {
          this.errorMessage = 'לא נמצא מוצר או פריט פיד לברקוד הזה.';
        } else {
          this.errorMessage = 'טעינת התצוגה נכשלה.';
        }
      });
    }
  }

  async fetchMissing(): Promise<void> {
    const code = this.barcode.trim();
    if (code.length === 0 || !this.result?.canFetchMissing) {
      return;
    }

    this.fetchingMissing = true;
    this.errorMessage = '';
    this.statusMessage = '';
    try {
      const preview = await firstValueFrom(this.api.previewProductName(code, true));
      this.finishFetch(() => {
        this.applyResult(preview);
        this.statusMessage = preview.fetchedMissing
          ? 'נמשך שם חסר מהאתר.'
          : 'אין ניסיון חסר למשוך.';
      });
    } catch {
      this.finishFetch(() => {
        this.errorMessage = 'משיכת שם מהאתר נכשלה.';
      });
    }
  }

  useCandidate(source: string): void {
    const name =
      source === 'rules'
        ? (this.result?.rulesTitle || this.result?.title || '').trim()
        : (this.candidateFor(source)?.name ?? '').trim();
    if (name.length === 0) {
      return;
    }

    this.manualDraft = name;
    this.errorMessage = '';
    this.statusMessage = 'הועתק לשדה העריכה. שמרו רק אם צריך לכוון ידנית.';
  }

  async saveManual(): Promise<void> {
    const productId = this.result?.productId;
    const name = this.manualDraft.trim();
    if (productId == null || name.length === 0) {
      return;
    }

    this.saving = true;
    this.errorMessage = '';
    this.statusMessage = '';
    try {
      await firstValueFrom(
        this.api.setDisplayName({
          productId,
          manualName: name
        })
      );
      await this.reloadPreview('נשמר שם ידני.');
    } catch {
      this.finishSave(() => {
        this.errorMessage = 'שמירת השם נכשלה.';
      });
    }
  }

  async clearManual(): Promise<void> {
    const productId = this.result?.productId;
    if (productId == null) {
      return;
    }

    this.saving = true;
    this.errorMessage = '';
    this.statusMessage = '';
    try {
      await firstValueFrom(
        this.api.setDisplayName({
          productId,
          manualName: null
        })
      );
      await this.reloadPreview('בוטל שם ידני. חזרה לבחירה אוטומטית.');
    } catch {
      this.finishSave(() => {
        this.errorMessage = 'ביטול השם הידני נכשל.';
      });
    }
  }

  sourceLabel(source: string | null | undefined): string {
    switch (source) {
      case 'manual':
        return 'ידני';
      case 'shufersal':
        return 'שופרסל';
      case 'rami_levy':
        return 'רמי לוי';
      case 'rules':
        return 'כללים';
      default:
        return 'לא נקבע';
    }
  }

  statusLabel(status: string | null | undefined): string {
    switch (status) {
      case 'found':
        return 'נמצא';
      case 'not_found':
        return 'לא נמצא';
      case 'error':
        return 'שגיאה';
      default:
        return 'לא נמשך';
    }
  }

  candidateFor(source: string): StorefrontNameCandidate | null {
    return this.result?.storefronts?.find((row) => row.source === source) ?? null;
  }

  formatSize(quantity: number | null, unitQty: string | null): string {
    if (quantity == null && !unitQty) return '—';
    if (quantity == null) return unitQty ?? '—';
    if (!unitQty) return String(quantity);
    return `${quantity} ${unitQty}`;
  }

  get mismatchesHasPrev(): boolean {
    return this.mismatchesOffset > 0;
  }

  get mismatchesHasNext(): boolean {
    return this.mismatchesOffset + this.mismatches.length < this.mismatchesTotal;
  }

  mismatchesPrevOffset(): number {
    return Math.max(0, this.mismatchesOffset - this.mismatchesPageSize);
  }

  mismatchesNextOffset(): number {
    return this.mismatchesOffset + this.mismatchesPageSize;
  }

  private async reloadPreview(okMessage: string): Promise<void> {
    const code = this.barcode.trim();
    const preview = await firstValueFrom(this.api.previewProductName(code));
    this.finishSave(() => {
      this.applyResult(preview);
      this.statusMessage = okMessage;
    });
  }

  private applyResult(preview: NamePreviewResult): void {
    this.result = preview;
    this.manualDraft = preview.displayNameManual ?? preview.title ?? '';
  }

  private finish(update: () => void): void {
    this.zone.run(() => {
      update();
      this.loading = false;
      this.appRef.tick();
    });
  }

  private finishFetch(update: () => void): void {
    this.zone.run(() => {
      update();
      this.fetchingMissing = false;
      this.appRef.tick();
    });
  }

  private finishSave(update: () => void): void {
    this.zone.run(() => {
      update();
      this.saving = false;
      this.appRef.tick();
    });
  }

  private finishMismatches(update: () => void): void {
    this.zone.run(() => {
      update();
      this.mismatchesLoading = false;
      this.appRef.tick();
    });
  }
}
